import * as THREE from 'three';
import { FLARES } from '../core/SimConfig.js';
import { rand } from '../utils.js';

const _nodeWorldPos = new THREE.Vector3();
const _bodyDown = new THREE.Vector3();

// ── CountermeasureDispenser ─────────────────────────────────────────────────
//
// Owns the trigger-to-flare timing for an aircraft. Resolves dispenser nodes
// from the aircraft model, and while `firing` is true ejects one flare from
// each node on a fixed interval (with a rising-edge shot on initial press).
//
// The interval cooldown is global to the dispenser (not gated on `firing`),
// so releasing and rapidly re-pressing the key cannot bypass the cadence —
// a fresh press only fires if the cooldown has fully elapsed.
//
// FlareSystem itself only knows how to spawn/integrate/render flares; the
// per-aircraft cadence and per-node origins live here so AI pilots that lack
// dispenser nodes can drive the same FlareSystem with their own logic.

export default class CountermeasureDispenser {
  /**
   * @param {{
   *   model: THREE.Object3D,
   *   aircraft: import('./Aircraft.js').default,
   *   flareSystem: import('../fx/FlareSystem.js').default,
   *   nodeNames?: string[],
   *   interval?: number,
   *   flares?: 'none' | 'full',
   *   unlimited?: boolean,
   * }} opts
   */
  constructor({ model, aircraft, flareSystem, nodeNames = FLARES.dispenserNodes, interval = FLARES.dispenseInterval, flares = 'full', unlimited = false }) {
    this.aircraft = aircraft;
    this.flareSystem = flareSystem;
    this.interval = interval;
    // Optional MissionStats sink (combat scenarios) — counts flares dispensed.
    this.stats = null;

    // 'none' loadout flies with empty dispensers; 'full' (or unspecified) gets
    // the full count. When `unlimited` is true the count never depletes.
    this.count = flares === 'none' ? 0 : FLARES.capacity;
    this.unlimited = unlimited;

    this._nodes = [];
    for (const name of nodeNames) {
      const n = model.getObjectByName(name);
      if (n) this._nodes.push(n);
      else console.warn(`CountermeasureDispenser: dispenser node "${name}" not found in model`);
    }

    this.firing = false;
    // Time remaining until the next shot is allowed. Always counts down,
    // even when `firing` is false, so spam-tapping can't bypass the cadence.
    this._cooldown = 0;
    // Per-node pending fire delays. Counted down each tick; a node fires
    // when its delay reaches 0 and the trigger is still held.
    this._pendingDelay = new Float32Array(this._nodes.length);
    this._pendingFire = new Uint8Array(this._nodes.length);
  }

  update(dt) {
    if (this._cooldown > 0) this._cooldown = Math.max(0, this._cooldown - dt);
    if (!this.flareSystem || this._nodes.length === 0) return;

    // Schedule a new staggered burst whenever the trigger is held and the
    // cooldown has elapsed. Each node gets its own random sub-interval delay
    // so the flares leave the plane at slightly different moments instead of
    // in lockstep.
    if (this.firing && this._cooldown <= 0) {
      for (let n = 0; n < this._nodes.length; n++) {
        this._pendingDelay[n] = rand(FLARES.dispenseJitter[0], FLARES.dispenseJitter[1]);
        this._pendingFire[n] = 1;
      }
      this._cooldown = this.interval;
    }

    this._tickPending(dt);
  }

  _tickPending(dt) {
    const aircraft = this.aircraft;
    for (let n = 0; n < this._nodes.length; n++) {
      if (!this._pendingFire[n]) continue;
      this._pendingDelay[n] -= dt;
      if (this._pendingDelay[n] > 0) continue;

      // Out of flares — drop the pending shot without ejecting anything.
      if (!this.unlimited && this.count <= 0) { this._pendingFire[n] = 0; continue; }

      // Recompute eject velocity per shot — aircraft attitude/velocity may
      // have shifted across the staggered window.
      _bodyDown.copy(aircraft.upVector).multiplyScalar(-FLARES.ejectSpeed);
      const vx = aircraft.velocity.x + _bodyDown.x;
      const vy = aircraft.velocity.y + _bodyDown.y;
      const vz = aircraft.velocity.z + _bodyDown.z;
      this._nodes[n].getWorldPosition(_nodeWorldPos);
      this.flareSystem.dispense(_nodeWorldPos.x, _nodeWorldPos.y, _nodeWorldPos.z, vx, vy, vz);
      this._pendingFire[n] = 0;
      this.stats?.recordFlare();
      if (!this.unlimited) this.count--;
    }
  }
}
